import type { ReactNode } from 'react';
import { Button } from './Button';
import { cx } from './cx';

/**
 * Os três estados que toda lista precisa ter (RNF-F04). Ficam juntos de
 * propósito: quem for criar uma lista nova encontra os três no mesmo import e
 * não "esquece" o vazio ou o erro.
 */

export function LoadingState({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-3 py-6">
      <span className="sr-only">{label}</span>
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-16 animate-pulse rounded-md bg-surface-raised" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-border px-6 py-10 text-center">
      <p className="text-base font-medium text-text">{title}</p>
      {description && <p className="max-w-md text-sm text-text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Não foi possível carregar',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-2 rounded-md border border-border bg-danger-soft px-4 py-4"
    >
      <p className="text-sm font-medium text-danger">{title}</p>
      {/* A mensagem vem sempre do catálogo traduzido — nunca do servidor cru. */}
      <p className="text-sm text-text">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Tentar novamente
        </Button>
      )}
    </div>
  );
}

export function InlineMessage({
  tone,
  children,
}: {
  tone: 'info' | 'success' | 'warning' | 'danger';
  children: ReactNode;
}) {
  const tones = {
    info: 'bg-accent-soft text-accent',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
  } as const;

  return (
    <p
      role={tone === 'danger' ? 'alert' : undefined}
      className={cx('rounded-md px-3 py-2 text-sm', tones[tone])}
    >
      {children}
    </p>
  );
}
